import React, { useState } from 'react';
import { CloseIcon, EyeIcon, EyeSlashIcon, SunIcon, MoonIcon, SystemIcon, GoogleIcon, ArrowPathIcon, DownloadIcon } from './icons';
import ToggleSwitch from './ToggleSwitch';

type Theme = 'light' | 'dark' | 'system';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentVersion: string;
  onSaveVersion: (version: string) => void;
  hideAnswered: boolean;
  onToggleHideAnswered: (value: boolean) => void;
  currentTheme: Theme;
  onSetTheme: (theme: Theme) => void;
  // Google Drive Sync props
  isSignedIn: boolean;
  isSyncing: boolean;
  lastSyncDate: Date | null;
  userProfile: gapi.auth2.BasicProfile | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onSyncNow: () => void;
  onRestore: () => void;
  isAutoSyncEnabled: boolean;
  onToggleAutoSync: (enabled: boolean) => void;
}

const BIBLE_VERSIONS = ['NIV', 'KJV', 'ESV', 'NLT', 'MSG', 'NASB'];

const SettingsModal: React.FC<SettingsModalProps> = ({ 
    isOpen, onClose, currentVersion, onSaveVersion, 
    hideAnswered, onToggleHideAnswered,
    currentTheme, onSetTheme,
    isSignedIn, isSyncing, lastSyncDate, userProfile,
    onSignIn, onSignOut, onSyncNow, onRestore,
    isAutoSyncEnabled, onToggleAutoSync
}) => {
  const [selectedVersion, setSelectedVersion] = useState(currentVersion);

  if (!isOpen) return null;

  const handleSave = () => {
    onSaveVersion(selectedVersion);
    onClose();
  };

  const ThemeButton = ({ theme, label, icon }: { theme: Theme, label: string, icon: React.ReactNode }) => {
    const isActive = currentTheme === theme;
    return (
      <button
        onClick={() => onSetTheme(theme)}
        className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
          isActive ? 'bg-indigo-100 dark:bg-indigo-900/50 border-indigo-500' : 'bg-gray-100 dark:bg-gray-700/50 border-transparent hover:border-gray-300 dark:hover:border-gray-500'
        }`}
      >
        {icon}
        <span className={`text-sm font-medium ${isActive ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
      </button>
    );
  };
  
  const getSyncStatusText = () => {
      if (isSyncing) return "Syncing...";
      if (!isSignedIn) return "Not signed in.";
      if (lastSyncDate) return `Last synced: ${lastSyncDate.toLocaleTimeString()}`;
      return "Ready to sync.";
  };


  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-auto relative flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Settings</h2>
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition">
            <CloseIcon className="w-7 h-7" />
          </button>
        </div>
        <div className="flex-grow overflow-y-auto p-6 space-y-6">
            {/* GOOGLE DRIVE SYNC SECTION */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Google Drive Sync
              </label>
              <div className="p-4 bg-gray-100 dark:bg-gray-900/50 rounded-lg">
                {isSignedIn && userProfile ? (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <img src={userProfile.getImageUrl()} alt="User" className="w-12 h-12 rounded-full"/>
                            <div>
                                <p className="font-semibold text-gray-800 dark:text-gray-100">{userProfile.getName()}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{userProfile.getEmail()}</p>
                            </div>
                        </div>
                        <div className="text-center text-sm text-gray-500 dark:text-gray-400 p-2 bg-gray-200 dark:bg-gray-700 rounded-md">
                           {getSyncStatusText()}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={onSyncNow} disabled={isSyncing} className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                                <ArrowPathIcon className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`}/> Sync Now
                            </button>
                            <button onClick={onRestore} disabled={isSyncing} className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                                <DownloadIcon className="w-4 h-4"/> Restore
                            </button>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              Automatic Sync
                          </span>
                          <ToggleSwitch
                              isChecked={isAutoSyncEnabled}
                              onChange={onToggleAutoSync}
                              ariaLabel="Toggle automatic Google Drive sync"
                          />
                        </div>
                        <button onClick={onSignOut} className="w-full px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60">
                            Sign Out
                        </button>
                    </div>
                ) : (
                    <button onClick={onSignIn} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50">
                        <GoogleIcon className="w-5 h-5"/> Sign in with Google
                    </button>
                )}
              </div>
            </div>

            {/* Other settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Appearance
              </label>
              <div className="flex items-center gap-2">
                <ThemeButton theme="light" label="Light" icon={<SunIcon className="w-6 h-6"/>} />
                <ThemeButton theme="dark" label="Dark" icon={<MoonIcon className="w-6 h-6"/>} />
                <ThemeButton theme="system" label="System" icon={<SystemIcon className="w-6 h-6"/>} />
              </div>
            </div>
           <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Preferences
                </label>
                 <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-900/50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {hideAnswered ? <EyeSlashIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" /> : <EyeIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                        <span className="text-sm text-gray-800 dark:text-gray-200">Hide Answered Prayers</span>
                    </div>
                    <ToggleSwitch isChecked={hideAnswered} onChange={onToggleHideAnswered} ariaLabel="Toggle answered prayers" />
                </div>
           </div>
          <div>
            <label htmlFor="bible-version" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Favorite Bible Version
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">This version will be used when Gemini reads scripture aloud.</p>
            <select
              id="bible-version"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="mt-1 block w-full bg-white text-gray-900 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {BIBLE_VERSIONS.map(version => (
                <option key={version} value={version}>
                  {version}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold py-2 px-4 rounded-md border border-gray-300 dark:border-gray-500 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-500">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-md shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-300 dark:focus:ring-indigo-800">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;